/**
 * Typing kept as the page goes (fix-next 1, tables U, K and M; tenet 3): a page's typing not stored yet, written to
 * `localStorage` in the very task of the event that may end the page, since a write to IndexedDB started then may never
 * commit (the fix-next 1 log's experiments). Another page merges it into the store when its session takes the lock, by
 * table R's three-way rule (W4), so it never writes over another tab's change. Pure: the storage is injected.
 */
import { isId } from '@cockatrice/storage';
import type { NoteEdit, NoteTarget } from '@cockatrice/storage';

/** What every page's record key starts with; the rest is the page's own ID (principle 3). */
export const UNSAVED_PREFIX = 'cockatrice.unsaved.';

/**
 * The record's format; a later build's is left as it is (M4). Part 19e, deep-fix r1, CK9: version 2 adds the composers'
 * words; a version 1 record reads as holding none.
 */
export const UNSAVED_VERSION = 2;

/** The part of Web Storage the records need (`localStorage`); any call may throw (none, refused, full). */
export interface UnsavedStorage {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The open chapter's typing not stored yet: its text, the {@link textHash} of the text the store certainly held from this
 * editor (`base`: what it opened, or last saved as the chapter), and of the save running, which may never have committed
 * (`saving`, null with none; *deep-fix r1*). `diverged`: the chapter changed under the editor (W4), so the text never goes
 * to the chapter.
 */
export interface UnsavedChapter {
  readonly chapterId: string;
  readonly bookId: string;
  readonly text: string;
  readonly base: string;
  readonly saving: string | null;
  readonly diverged: boolean;
}

/**
 * A note's draft not stored yet (part 19c, table W): its target, base and value, and the value of the write running for
 * it, which may never have committed (`saving`, null with none; *deep-fix r2*).
 */
export interface UnsavedNote extends NoteEdit {
  readonly saving: string | null;
}

/**
 * Part 19e, deep-fix r1, table CK: a composer's words, never a note before Save note: the composer (`notebook:<book>`,
 * `chapter:<chapter>` or `reply:<first note>`), its book, its words (never empty).
 */
export interface UnsavedComposer {
  readonly key: string;
  readonly bookId: string;
  readonly text: string;
}

/** A page's typing not stored yet (table U): the open chapter's, or none, the notes' drafts and the composers' words. */
export interface Unsaved {
  readonly chapter: UnsavedChapter | null;
  readonly notes: readonly UnsavedNote[];
  readonly composers: readonly UnsavedComposer[];
}

/** What a stored record reads as: a record, a later build's (M4), or damaged (M4). */
export type ReadUnsaved = { readonly kind: 'read'; readonly unsaved: Unsaved } | { readonly kind: 'later' } | { readonly kind: 'damaged' };

/**
 * A text's hash for K2's comparison: its length and 64 bits from two 32-bit multiply-xor lanes over its UTF-16 code
 * units. Not a security check: anyone who can write the record can write the store; it only has to tell texts apart.
 */
export function textHash(text: string): string {
  let a = 0xdeadbeef;
  let b = 0x41c6ce57;
  for (let at = 0; at < text.length; at++) {
    const unit = text.charCodeAt(at);
    a = Math.imul(a ^ unit, 0x9e3779b1);
    b = Math.imul(b ^ unit, 0x5f356495);
  }
  a = Math.imul(a ^ (a >>> 16), 0x85ebca6b) ^ Math.imul(b ^ (b >>> 13), 0xc2b2ae35);
  b = Math.imul(b ^ (b >>> 16), 0x85ebca6b) ^ Math.imul(a ^ (a >>> 13), 0xc2b2ae35);
  const hex = (lane: number): string => (lane >>> 0).toString(16).padStart(8, '0');
  return `${String(text.length)}:${hex(a)}${hex(b)}`;
}

const HASH = /^(?:0|[1-9]\d*):[0-9a-f]{16}$/u;

/** The record as stored: its format version first. */
export function serializeUnsaved(unsaved: Unsaved): string {
  return JSON.stringify({ version: UNSAVED_VERSION, chapter: unsaved.chapter, notes: unsaved.notes, composers: unsaved.composers });
}

/** Whether `value` is a plain object holding exactly `keys`. */
function hasExactly(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const own = Object.keys(value);
  return own.length === keys.length && keys.every((key) => Object.hasOwn(value, key));
}

function readChapter(value: unknown): UnsavedChapter | null | undefined {
  if (value === null) return null;
  if (!hasExactly(value, ['chapterId', 'bookId', 'text', 'base', 'saving', 'diverged'])) return undefined;
  const { chapterId, bookId, text, base, saving, diverged } = value;
  if (!isId(chapterId) || !isId(bookId) || typeof text !== 'string' || typeof diverged !== 'boolean') return undefined;
  if (typeof base !== 'string' || !HASH.test(base)) return undefined;
  if (saving !== null && (typeof saving !== 'string' || !HASH.test(saving))) return undefined;
  return { chapterId, bookId, text, base, saving, diverged };
}

/**
 * A draft's field. Part 19e, table MU: an earlier build's draft of a chapter's notes is the text of the note under the
 * chapter's ID (MU1, MG2), of a book note's name its title (MU4); the fields of now as they are.
 */
function readTarget(value: unknown): NoteTarget | undefined {
  if (!hasExactly(value, ['kind', 'id', 'field'])) return undefined;
  const { kind, id, field } = value;
  if (!isId(id)) return undefined;
  if (kind === 'chapter' && field === 'synopsis') return { kind, id, field };
  if (kind === 'chapter' && field === 'notes') return { kind: 'note', id, field: 'text' };
  if (kind === 'note' && field === 'name') return { kind, id, field: 'title' };
  if (kind === 'note' && (field === 'text' || field === 'title' || field === 'group')) return { kind, id, field };
  return undefined;
}

function readNote(value: unknown): UnsavedNote | undefined {
  if (!hasExactly(value, ['target', 'base', 'value', 'saving'])) return undefined;
  const { target: field, base, value: text, saving } = value;
  const target = readTarget(field);
  if (target === undefined || typeof base !== 'string' || typeof text !== 'string') return undefined;
  return saving === null || typeof saving === 'string' ? { target, base, value: text, saving } : undefined;
}

/** A composer's key: the Notebook's of a book, a chapter's, or a thread's reply. */
export const COMPOSER_KEY = /^(?:notebook|chapter|reply):[0-9a-f]{32}$/u;

function readComposer(value: unknown): UnsavedComposer | undefined {
  if (!hasExactly(value, ['key', 'bookId', 'text'])) return undefined;
  const { key, bookId, text } = value;
  if (typeof key !== 'string' || !COMPOSER_KEY.test(key) || !isId(bookId) || typeof text !== 'string' || text === '') return undefined;
  return { key, bookId, text };
}

/**
 * Reads a stored record (the boundary: the storage may hold anything): `later` for a later format version, `damaged`
 * for anything that isn't exactly a record of this one (M4). Never throws.
 */
export function readUnsaved(raw: string): ReadUnsaved {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return { kind: 'damaged' };
  }
  if (typeof value !== 'object' || value === null) return { kind: 'damaged' };
  const version: unknown = Object.getOwnPropertyDescriptor(value, 'version')?.value;
  if (typeof version === 'number' && version > UNSAVED_VERSION) return { kind: 'later' };
  // CK9: version 1 holds no composers.
  const keys = version === 1 ? ['version', 'chapter', 'notes'] : ['version', 'chapter', 'notes', 'composers'];
  if ((version !== 1 && version !== UNSAVED_VERSION) || !hasExactly(value, keys)) return { kind: 'damaged' };
  const { chapter: part, notes: drafts } = value;
  const typed = version === 1 ? [] : value['composers'];
  if (!Array.isArray(drafts) || !Array.isArray(typed)) return { kind: 'damaged' };
  const chapter = readChapter(part);
  if (chapter === undefined) return { kind: 'damaged' };
  const notes: UnsavedNote[] = [];
  for (const each of drafts) {
    const note = readNote(each);
    if (note === undefined) return { kind: 'damaged' };
    notes.push(note);
  }
  const composers: UnsavedComposer[] = [];
  for (const each of typed) {
    const composer = readComposer(each);
    if (composer === undefined) return { kind: 'damaged' };
    composers.push(composer);
  }
  return { kind: 'read', unsaved: { chapter, notes, composers } };
}

/** What merging a record's chapter part does (table K): nothing, the text stored as the chapter, or kept as a copy. */
export type Recovery = 'none' | 'chapter' | 'copy';

/**
 * Table K: what another page's typing does to the store, given the chapter's stored text (`stored`: none, or null when
 * unreadable) and whether the chapter is live in the record's book (`live`). Table R's rule (W4): the text goes to the
 * chapter only when the store holds the text this editor grew from or was storing (K2); nothing more is needed only when
 * the typing is the text the store certainly held, never one a save was still storing (K4, *deep-fix r1*); otherwise a
 * copy (K3).
 */
export function recoveryOf(chapter: UnsavedChapter, stored: string | null | undefined, live: boolean): Recovery {
  if (stored === chapter.text) return 'none';
  if (typeof stored !== 'string') return 'copy';
  const held = textHash(stored);
  if (!chapter.diverged && live && (held === chapter.base || held === chapter.saving)) return 'chapter';
  return textHash(chapter.text) === chapter.base ? 'none' : 'copy';
}

/**
 * Deep-fix r2: a note's draft as table W merges it, given the field's stored text (`stored`, undefined when its record is
 * gone): a write running as the page went that landed is what the typing grew from (W2), so it isn't joined (W5).
 */
export function noteEditOf(note: UnsavedNote, stored: string | undefined): NoteEdit {
  const base = note.saving !== null && stored === note.saving ? note.saving : note.base;
  return { target: note.target, base, value: note.value };
}
